import { Timestamp, addDoc, collection, deleteDoc, doc, getDoc, getDocs, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import { ContestReward } from '../types/contests';

export type ContestRewardPayload = {
  type: 'contest' | 'discount';
  title: string;
  description: string;
  pixRequired: number;
  startAt: number;
  endAt: number;
  isActive: boolean;
  partnerName?: string;
  partnerLogoUrl?: string;
  discountCode?: string;
  externalUrl?: string;
  maxEntries?: number | null;
  allowMultipleEntries?: boolean;
  targetLanguage?: string;
  createdByUid?: string;
  imageUrl?: string;
  i18n?: { title?: Record<string, string>; description?: Record<string, string> };
};

export async function listContests() {
  try {
    const q = query(collection(db, 'contests_rewards'), orderBy('startAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err: any) {
    // If index error, fallback to simple query
    if (err?.code === 'failed-precondition' || err?.message?.includes('index')) {
      console.warn('Index not found, using simple query. Please create index for (startAt)');
      const q = query(collection(db, 'contests_rewards'));
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort in memory by startAt descending
      return items.sort((a: any, b: any) => {
        const aStart = a.startAt?.toMillis ? a.startAt.toMillis() : a.startAt;
        const bStart = b.startAt?.toMillis ? b.startAt.toMillis() : b.startAt;
        return (bStart || 0) - (aStart || 0);
      });
    }
    throw err;
  }
}

export async function getContest(id: string) {
  const d = await getDoc(doc(db, 'contests_rewards', id));
  return d.exists() ? { id: d.id, ...d.data() } : null;
}

// Helper to remove undefined values from object (Firestore doesn't accept undefined)
function removeUndefined(obj: any): any {
  const cleaned: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  }
  return cleaned;
}

export async function createContest(payload: ContestRewardPayload, file?: File) {
  const refDoc = doc(collection(db, 'contests_rewards'));
  let imageUrl = payload.imageUrl || '';
  
  // Upload image if provided
  if (file) {
    const imgRef = ref(storage, `contests_rewards/${refDoc.id}/image`);
    await uploadBytes(imgRef, file);
    imageUrl = await getDownloadURL(imgRef);
  }
  
  const data = removeUndefined({
    type: payload.type,
    title: payload.title,
    description: payload.description,
    pixRequired: payload.pixRequired,
    startAt: Timestamp.fromMillis(payload.startAt),
    endAt: Timestamp.fromMillis(payload.endAt),
    isActive: payload.isActive,
    partnerName: payload.partnerName,
    partnerLogoUrl: payload.partnerLogoUrl,
    discountCode: payload.discountCode,
    externalUrl: payload.externalUrl,
    maxEntries: payload.maxEntries,
    allowMultipleEntries: payload.allowMultipleEntries,
    targetLanguage: payload.targetLanguage,
    i18n: payload.i18n,
    createdByUid: payload.createdByUid,
    imageUrl: imageUrl || undefined,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  await setDoc(refDoc, data);
  return refDoc.id;
}

export async function updateContest(id: string, payload: Partial<ContestRewardPayload>, file?: File) {
  const updates: any = {
    updatedAt: serverTimestamp()
  };
  
  // Upload image if provided
  if (file) {
    const imgRef = ref(storage, `contests_rewards/${id}/image`);
    await uploadBytes(imgRef, file);
    updates.imageUrl = await getDownloadURL(imgRef);
  }
  
  // Only include defined fields
  if (payload.type !== undefined) updates.type = payload.type;
  if (payload.title !== undefined) updates.title = payload.title;
  if (payload.description !== undefined) updates.description = payload.description;
  if (payload.pixRequired !== undefined) updates.pixRequired = payload.pixRequired;
  if (payload.isActive !== undefined) updates.isActive = payload.isActive;
  if (payload.partnerName !== undefined) updates.partnerName = payload.partnerName;
  if (payload.partnerLogoUrl !== undefined) updates.partnerLogoUrl = payload.partnerLogoUrl;
  if (payload.discountCode !== undefined) updates.discountCode = payload.discountCode;
  if (payload.externalUrl !== undefined) updates.externalUrl = payload.externalUrl;
  if (payload.maxEntries !== undefined) updates.maxEntries = payload.maxEntries;
  if (payload.allowMultipleEntries !== undefined) updates.allowMultipleEntries = payload.allowMultipleEntries;
  if (payload.targetLanguage !== undefined) updates.targetLanguage = payload.targetLanguage;
  if (payload.i18n !== undefined) updates.i18n = payload.i18n;
  if (payload.imageUrl !== undefined) updates.imageUrl = payload.imageUrl;
  
  // Convert timestamps if provided
  if (payload.startAt !== undefined) {
    updates.startAt = Timestamp.fromMillis(payload.startAt);
  }
  if (payload.endAt !== undefined) {
    updates.endAt = Timestamp.fromMillis(payload.endAt);
  }
  
  // Remove undefined values before sending
  const cleaned = removeUndefined(updates);
  await updateDoc(doc(db, 'contests_rewards', id), cleaned);
}

export async function deleteContest(id: string) {
  await deleteDoc(doc(db, 'contests_rewards', id));
}

export async function toggleContestActive(id: string, isActive: boolean) {
  await updateDoc(doc(db, 'contests_rewards', id), {
    isActive,
    updatedAt: serverTimestamp()
  });
}

